import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
} from 'react';
import {
  fetchRoleInBrand,
  fetchUser as apiFetchUser,
  getCampaignStatsByBrand,
} from '../services/userService';
import { IUser } from '../interfaces/User';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserRole, BrandMemberRole } from '../types/enums';
import { getUserSubscription } from '../services/linkService';
import { AuthService } from '../services/authService';
import { TokenStore } from '../services/TokenStore';
import { isPublicRoute } from '../routes/routeUtils';
import { impersonateBrand } from '../services/BrandService';
import { useNotificationContext } from './NotificationContext';

interface UserContextType {
  user: IUser | null;
  setUser: (user: IUser | null) => void;
  loading: boolean;
  loadError: boolean;
  fetchUser: () => Promise<void>;
  updateUserImage: (image: string) => void;
  updateBrandLogoProfile: (logo: string) => void;
  isImpersonating: boolean;
  impersonateBrandId: string | null;
  impersonateBrandName: string | null;
  exitImpersonation: () => Promise<void>;
  setCampaignStats: (stats: any) => void;
  campaignStats: any;
  refreshCampaignStats: () => Promise<void>;
  updateBrandNameProfile: (name: string) => void;
  isDummyUser: boolean;
}

export const UserContext = createContext<UserContextType | undefined>(
  undefined,
);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const lastCampaignStatsRef = useRef<any>(null);
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserId } = useNotificationContext();
  const userIdRef = useRef<string | null>(null);
  const hasFetchedOnce = useRef(false);
  const updateUserImage = (image: string) => {
    if (!image) return; // ⛔ guard

    setUser((prev) =>
      prev
        ? {
            ...prev,
            profileImage: image,
            profileImageVersion: Date.now(), // cache-bust safely
          }
        : prev,
    );
  };

  const updateBrandLogoProfile = (logo: string) => {
    setUser((prev) =>
      prev && prev.brand
        ? {
            ...prev,
            brand: { ...prev.brand, brandLogo: logo },
          }
        : prev,
    );
  };
  const updateBrandNameProfile = (name: string) => {
    if (!name) return;

    setUser((prev) =>
      prev && prev.brand
        ? {
            ...prev,
            brand: {
              ...prev.brand,
              name,
            },
          }
        : prev,
    );
  };

  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonateBrandId, setImpersonateBrandId] = useState<string | null>(
    null,
  );
  const [impersonateBrandName, setImpersonateBrandName] = useState<
    string | null
  >(null);
  const [campaignStats, setCampaignStats] = useState<{
    live: number;
    scheduled: number;
    completed: number;
    draft: number;
    paused: number;
    total: number;
  } | null>(null);
  const [isDummyUser, setIsDummyUser] = useState(false);
  useEffect(() => {
    if (userIdRef.current !== user?._id) {
      userIdRef.current = user?._id || null;
      setUserId(user?._id || '');
    }
  }, [user?._id]);
  useEffect(() => {
    if (!user?.brand?._id) return;

    refreshCampaignStats();
  }, [user?.brand?._id]);

  useEffect(() => {
    const session = TokenStore.getImpersonationSession();

    if (session) {
      setIsImpersonating(true);
      setImpersonateBrandId(session.brandId);
      setImpersonateBrandName(session.brandName);
    } else {
      setIsImpersonating(false);
      setImpersonateBrandId(null);
      setImpersonateBrandName(null);
    }
  }, [location.pathname]);

  const loadingRef = useRef(false);
  const lastLoadedPathRef = useRef<string | null>(null);
  const bootstrapRefreshingRef = useRef(false);

  const loadUser = useCallback(
    async (path: string, forceReload = false) => {
      if (loadingRef.current) {
        return;
      }

      if (!forceReload && lastLoadedPathRef.current === path) {
        return;
      }

      loadingRef.current = true;
      setLoading(true);
      setLoadError(false);
      try {
        const userData = await apiFetchUser();
        if (userData._id === '697c75999f382122e2f996b7') {
          setIsDummyUser(true);
        }
        if (!userData) {
          lastLoadedPathRef.current = null;
          setUser(null);
          if (!isPublicRoute(path)) {
            navigate('/signin');
          }
          return;
        }

        const isCurrentlyImpersonating = TokenStore.isImpersonating();
        const impersonateBrandId =
          TokenStore.getImpersonationSession()?.brandId ?? null;

        if (isCurrentlyImpersonating && userData) {
          if (userData.role !== UserRole.BrandUser) {
            userData.role = UserRole.BrandUser;
          }

          if (!userData.brand && impersonateBrandId) {
            userData.brand = { _id: impersonateBrandId } as { _id: string };
          }
        }

        if (
          userData &&
          userData.brand &&
          userData.role === UserRole.BrandUser
        ) {
          if (isCurrentlyImpersonating) {
            userData.brandMemberRole = BrandMemberRole.Admin;
          } else {
            const rawBrandId =
              typeof userData.brand === 'object'
                ? userData.brand._id
                : (userData.brand as unknown as string);

            const normalizeRole = (
              role: unknown,
            ): BrandMemberRole | undefined => {
              if (role === BrandMemberRole.Admin || role === 'Admin')
                return BrandMemberRole.Admin;
              if (role === BrandMemberRole.Manager || role === 'Manager')
                return BrandMemberRole.Manager;
              if (role === BrandMemberRole.User || role === 'User')
                return BrandMemberRole.User;
              return undefined;
            };

            // Prefer deriving from /auth/me payload to avoid an extra network call.
            if (
              typeof userData.brand === 'object' &&
              Array.isArray(
                (userData.brand as unknown as { members?: unknown }).members,
              ) &&
              userData._id
            ) {
              const members = (
                userData.brand as unknown as {
                  members: Array<{
                    userId: string | { _id?: string } | null | undefined;
                    role?: unknown;
                  }>;
                }
              ).members;

              const member = members.find((m) => {
                const memberUserId =
                  typeof m.userId === 'string'
                    ? m.userId
                    : m.userId && typeof m.userId === 'object'
                      ? m.userId._id
                      : undefined;

                return memberUserId && memberUserId === userData._id;
              });

              const inferredRole = normalizeRole(member?.role);
              userData.brandMemberRole = inferredRole || BrandMemberRole.User;
            } else if (rawBrandId && userData._id) {
              // Fallback: ask the API (works for Mongo ObjectId strings)
              try {
                const role = await fetchRoleInBrand(userData._id, rawBrandId);
                userData.brandMemberRole =
                  normalizeRole(role) || BrandMemberRole.User;
              } catch {
                userData.brandMemberRole = BrandMemberRole.User;
              }
            } else {
              userData.brandMemberRole = BrandMemberRole.User;
            }
          }
        }

        try {
          const userSubscription = await getUserSubscription(userData._id);
          const hasSubscription =
            (userSubscription?.plan === 'monthly' ||
              userSubscription?.plan === 'yearly') &&
            userSubscription?.status !== 'canceled';

          userData.subscription = {
            plan: userSubscription?.plan || 'free',
            status: userSubscription?.status || 'inactive',
            hasSubscription,
          };
        } catch (subError) {
          userData.subscription = {
            plan: 'free',
            status: 'inactive',
            hasSubscription: false,
          };
        }

        lastLoadedPathRef.current = path;
        setUser(userData);
      } catch (err) {
        lastLoadedPathRef.current = null;
        if (!isPublicRoute(path)) {
          if (AuthService.isAuthenticated()) {
            // Transient failure (network/timeout/5xx): the tokens were kept by
            // AuthService.refreshToken(), so the session may still be valid.
            // Navigating to /signin here would bounce straight back through
            // the route guards — surface a retryable error instead.
            setLoadError(true);
          } else {
            // Session dead — tokens were cleared; signin lands on the form.
            navigate('/signin');
          }
        }
        setUser(null);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [navigate],
  );

  // A reload wipes TokenStore (memory only), so the admin comes back as
  // themselves on a brand route — which the route guards then reject. The
  // pinned brand id survives the reload, so re-mint the impersonation
  // tokens from the freshly refreshed admin session before loading the user.
  const restoreImpersonation = useCallback(async () => {
    const pinned = TokenStore.getPinnedBrand();
    if (!pinned || TokenStore.isImpersonating()) return;

    const tokens = await impersonateBrand(pinned.brandId).catch(() => null);

    if (!tokens) {
      // Admin can no longer impersonate this brand (deleted, permissions
      // changed): drop the pin and stay signed in as the admin.
      TokenStore.setImpersonationSession(null);
      return;
    }

    TokenStore.setImpersonationSession({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      brandId: pinned.brandId,
      brandName: pinned.brandName,
    });
    setIsImpersonating(true);
    setImpersonateBrandId(pinned.brandId);
    setImpersonateBrandName(pinned.brandName);
  }, []);

  const exitImpersonation = async () => {
    try {
      // TokenStore.getAccessToken() (the admin's own access token) was
      // never touched by setImpersonationSession — it's still sitting in
      // memory exactly as it was before impersonation started. Dropping
      // the impersonation session makes it the active token again
      // immediately, with no network round-trip — this matches the
      // original localStorage version's timing exactly. If that cached
      // admin token has since expired, the very next API call 401s and the
      // existing axios interceptor's refresh-on-401 logic transparently
      // refreshes it, same as it would have for a stale token restored
      // from localStorage.
      TokenStore.setImpersonationSession(null);

      setIsImpersonating(false);
      setImpersonateBrandId(null);
      setImpersonateBrandName(null);

      await loadUser(location.pathname);

      navigate('/admin/brand');
    } catch (error) {
      console.error('Error exiting impersonation:', error);
    }
  };

  const fetchUser = useCallback(() => {
    return loadUser(location.pathname, true);
  }, [loadUser, location.pathname]);

  useEffect(() => {
    if (hasFetchedOnce.current) return;
    const path = location.pathname;

    if (lastLoadedPathRef.current === path && user) {
      return;
    }

    if (isPublicRoute(path)) {
      setLoading(false);
      return;
    }

    if (AuthService.isAuthenticated()) {
      hasFetchedOnce.current = true;
      loadUser(path);
      return;
    }

    if (bootstrapRefreshingRef.current) {
      return;
    }
    bootstrapRefreshingRef.current = true;

    // No access token in memory (fresh reload) — try the httpOnly refresh
    // cookie before giving up on the session; access tokens no longer
    // survive a reload since they live in memory, not localStorage.
    AuthService.refreshToken()
      .catch(() => null)
      .then(async (token) => {
        bootstrapRefreshingRef.current = false;
        if (token) {
          hasFetchedOnce.current = true;
          await restoreImpersonation();
          loadUser(path);
        } else {
          navigate('/signin');
          setLoading(false);
        }
      });
  }, [location.pathname, navigate, loadUser, user, restoreImpersonation]);

  // if (loading && !isPublicRoute(location.pathname)) {
  //   return <Loading />;
  // }
  const refreshCampaignStats = async () => {
    if (!user?.brand?._id) return null;

    const stats = await getCampaignStatsByBrand(user.brand._id);
    if (stats) {
      lastCampaignStatsRef.current = stats;
      setCampaignStats(stats);
    }
    return stats;
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        loading,
        loadError,
        fetchUser,
        updateUserImage,
        updateBrandLogoProfile,
        isImpersonating,
        impersonateBrandId,
        impersonateBrandName,
        exitImpersonation,
        campaignStats: campaignStats ?? lastCampaignStatsRef.current,
        setCampaignStats,
        refreshCampaignStats,
        updateBrandNameProfile,
        isDummyUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
